import { InfoCircleOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import type { MenuProps } from 'antd';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Card from 'antd/lib/card';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Layout from 'antd/lib/layout';
import Row from 'antd/lib/row';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import React, { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WithControlledTooltip } from 'src/components/withControlledTooltip';
import { fromZonedTime } from 'date-fns-tz';
import { EnergyUsageTimeseriesDto, EnergyUsageTimeseriesValueDto } from 'src/domain/energy/interface';
import { MINUTE_MS } from 'src/domain/commonConst';
import { useAppContext } from '../../../AppContextProvider';
import { AbilityContext } from '../../../components/ability/can';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { DatePeriodSelectorWithPresets } from '../../../components/selectors/DatePeriodSelectorWithPresets/DatePeriodSelectorWithPresets';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { ViewSwitcher } from '../../../components/viewSwitcher/ViewSwitcher';
import { getComputerFormattedTime } from '../../../domain/date/date';
import { exportSitesEnergyUsageData, useSitesEnergyUsageForChartsQuery, useSitesEventsForChartsQuery } from '../../../domain/energy/queries';
import { useLocalStorage } from '../../../domain/useLocalStorage';
import { usePageLocation } from '../../usePageState';
import { EnergyConsumptionWidget } from '../components/analytics/EnergyConsumptionWidget';
import { EnergyDemandWidget } from '../components/analytics/EnergyDemandWidget';
import { resolveBaselineMarket } from '../../../domain/energy/baselineMarket';
import { EnergyUsageOverHourWidget } from '../components/analytics/EnergyUsageOverHourWidget';
import { RequestEnergyUsageModal } from '../components/analytics/RequestEnergyUsageModal';
import { UploadEnergyDataArchiveModal } from '../components/analytics/UploadEnergyDataArchiveModal';
import { UploadEnergyDataDominionModal } from '../components/analytics/UploadEnergyDataDominionModal';
import { UploadEnergyDataModal } from '../components/analytics/UploadEnergyDataModal';
import { ENERGY_USAGE_PAGE_CONST } from '../sidebar/constants';
import { useEnergyUsageSidebarFiltersContext } from '../sidebar/context/EnergyUsageSidebarFiltersContext';
import { EnergyUsageSidebar } from '../sidebar/EnergyUsageSidebar';
import { viewNavigationOptions } from './viewNavigationOptions';
import { calcEstimatedCSVRows } from '../../../domain/energy/calcEstimatedCSVRows';

const emptyEnergyDataObject: EnergyUsageTimeseriesDto = {
    enrollmentId: 'aggregate',
    fields: [
        'timestamp',
        'usageKw',
        'qualityOfReading',
        'flowDirection',
        'baselineCaiso10dUnadjustedKw',
        'baselineCaiso10dAdjustedKw',
        'performanceCaiso10dKw',
        'baseline3hKw',
        'performance3hKw',
        'baselineStaticKw',
        'performanceStaticKw',
    ],
    values: [],
};

type TModalView = 'greenbutton' | 'sharepoint' | 'dominion' | 'request-energy-usage' | 'none';

export const EnergyUsageDataAnalytics = () => {
    useDocumentTitle('Energy Usage Data');

    const { notification } = App.useApp();
    const location = useLocation();
    const navigate = useNavigate();
    const { queryToState } = usePageLocation();
    const pageState = queryToState(location.search);
    const ability = useContext(AbilityContext);

    const { date, handleDateChange, selectedSiteSans, selectedSiteOptions } = useEnergyUsageSidebarFiltersContext();
    const isSitesSelected = Boolean(selectedSiteSans.length);

    const [exportLoading, setExportLoading] = useState(false);

    const [modalView, setModalView] = useState<TModalView>('none');

    /** the selected days are built at the computer's current UTC offset, the same one the note shows */
    const utcOffset = getComputerFormattedTime(new Date(), 'xxx');
    const dateAtUtcOffset = useMemo(
        () => ({
            start: fromZonedTime(date.start, utcOffset),
            end: fromZonedTime(date.end, utcOffset),
        }),
        [date, utcOffset]
    );

    const queryParams = {
        sans: selectedSiteSans,
        siteIds: selectedSiteOptions.map(option => option.site.site_id),
        date: dateAtUtcOffset,
    };
    const sitesEnergyUsage = useSitesEnergyUsageForChartsQuery(queryParams, {
        keepPreviousData: false,
        refetchOnMount: false,
        enabled: false,
    });

    const sitesEnergyEvents = useSitesEventsForChartsQuery(queryParams, {
        keepPreviousData: false,
        refetchOnMount: false,
        enabled: false,
    });

    const isFetchingData = sitesEnergyUsage.isFetching || sitesEnergyEvents.isFetching;

    const energyData = sitesEnergyUsage.data?.length ? sitesEnergyUsage.data[0] : emptyEnergyDataObject;
    /** make all interval data is sorted */
    energyData.values = energyData.values.sort((a, b) => a[0] - b[0]);

    const eventsData = useMemo(() => (sitesEnergyEvents?.data?.length ? sitesEnergyEvents.data : []), [sitesEnergyEvents?.data]);

    /** charts draw in UTC, so every timestamp is moved by the current offset - computer's time without daylight saving jumps */
    const offsetMs = -new Date().getTimezoneOffset() * MINUTE_MS;
    const chartDateRange = useMemo(
        () => ({
            start: new Date(dateAtUtcOffset.start.valueOf() + offsetMs),
            end: new Date(dateAtUtcOffset.end.valueOf() + offsetMs),
        }),
        [dateAtUtcOffset, offsetMs]
    );
    const chartEnergyData: EnergyUsageTimeseriesDto = useMemo(
        () => ({
            ...energyData,
            values: energyData.values.map(([timestamp, ...rest]) => [timestamp + offsetMs, ...rest] as EnergyUsageTimeseriesValueDto),
        }),
        [energyData, offsetMs]
    );
    const chartEventsData = useMemo(
        () => eventsData.map(event => ({ ...event, start: new Date(new Date(event.start).valueOf() + offsetMs).toISOString() })),
        [eventsData, offsetMs]
    );

    const baselineMarket = useMemo(() => resolveBaselineMarket(selectedSiteOptions.map(option => option.site)), [selectedSiteOptions]);

    if (sitesEnergyUsage.isError) {
        notification.error({
            key: 'fetch-energy-data-error',
            message: sitesEnergyUsage.error.message || 'Cannot fetch energy data!',
        });
    }

    if (sitesEnergyEvents.isError) {
        notification.error({
            key: 'fetch-events-data-error',
            message: sitesEnergyEvents.error.message || 'Cannot fetch events data!',
        });
    }

    const exportWarning: string = useMemo(() => {
        if (!selectedSiteSans.length || !date?.start || !date?.end) return '';

        const MAX_ROWS_PER_EXPORT = 150_000;

        const estimatedRows = calcEstimatedCSVRows(date.start, date.end, selectedSiteSans.length);

        if (estimatedRows > MAX_ROWS_PER_EXPORT) {
            const roundedEstimatedRows = Math.round(estimatedRows / 1000) * 1000;
            return `Estimated amount of CSV rows is ${roundedEstimatedRows.toLocaleString()}. The limit is ${MAX_ROWS_PER_EXPORT.toLocaleString()}. Try to reduce the number of Enrollment IDs or select shorter date range.`;
        }
        return '';
    }, [date, selectedSiteSans]);

    const onViewChange = (view: string) => {
        const companyQuery = pageState.companyId ? `?companyId=${pageState.companyId}` : '';
        navigate(`${view}${companyQuery}`);
    };

    const onModalViewChange = (view: TModalView) => {
        setModalView(view);
    };

    const onExportEnergyData = async () => {
        setExportLoading(true);

        try {
            await exportSitesEnergyUsageData({
                sans: selectedSiteSans,
                date: dateAtUtcOffset,
            });
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            notification.error({
                key: 'fetch-energy-data-error',
                message: error.message || 'Cannot fetch energy data!',
            });
        }

        setExportLoading(false);
    };

    const triggerDataLoad = () => {
        sitesEnergyUsage.refetch();
        sitesEnergyEvents.refetch();
    };

    const { isFiltersSidebarEnabled, setSidebarCollapsed, layoutPaddingLeft } = useEnergyUsageSidebar();

    return (
        // Padding is needed to avoid content being hidden behind the sidebar
        <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
            {isFiltersSidebarEnabled && (
                <EnergyUsageSidebar
                    isChartsDataLoading={isFetchingData}
                    onLoadData={(siteIds, sites) => triggerDataLoad()}
                    onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                />
            )}

            <Layout.Content style={{ padding: '16px' }}>
                <PageHeader
                    pageTitle="Energy Usage Data"
                    extra={[
                        <ViewSwitcher
                            viewOptions={viewNavigationOptions(ability)}
                            key="energy-usage-data-view-switcher"
                            view={location.pathname}
                            handleViewMode={onViewChange}
                        />,
                    ]}
                    actions={[
                        <WithControlledTooltip
                            key="date-period-selector-tooltip"
                            title="Please select site first!"
                            extraVisibleCondition={!isSitesSelected}
                        >
                            <DatePeriodSelectorWithPresets
                                disabled={isFetchingData || !isSitesSelected}
                                onChange={handleDateChange}
                                selectedPeriod={date}
                            />
                        </WithControlledTooltip>,
                        <WithControlledTooltip key="reload-data-tooltip" title="Please select site first!" extraVisibleCondition={!isSitesSelected}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<SyncOutlined />}
                                onClick={() => triggerDataLoad()}
                                disabled={isFetchingData || !isSitesSelected}
                                data-cy="reload-data"
                            >
                                Load data
                            </Button>
                        </WithControlledTooltip>,
                        ability.can('upload', 'EnergyFile') && (
                            <UploadEnergyDataMenu key="upload-energy-menu" handleModalViewChange={onModalViewChange} />
                        ),
                        <WithControlledTooltip
                            key="download-csv-tooltip"
                            title={!isSitesSelected ? 'Please select site first!' : exportWarning}
                            extraVisibleCondition={!isSitesSelected || !!exportWarning}
                        >
                            <Button
                                size="large"
                                onClick={onExportEnergyData}
                                disabled={isFetchingData || !isSitesSelected || !!exportWarning}
                                icon={<DownloadOutlined />}
                                data-cy="download-data"
                                loading={exportLoading}
                            >
                                Download CSV
                            </Button>
                        </WithControlledTooltip>,
                    ]}
                />
                <div style={{ margin: '-24px 8px 8px 8px' }}>
                    <InfoCircleOutlined /> The chart’s data is displayed in your computer’s timezone - {getComputerFormattedTime(new Date(), 'zzz')}
                </div>

                {!isSitesSelected && (
                    <Card size="small">
                        <Row justify="center">
                            <Space direction="vertical">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={<div style={{ fontSize: '18px', fontWeight: '500' }}>Please select a site</div>}
                                />
                            </Space>
                        </Row>
                    </Card>
                )}

                {isSitesSelected && isFetchingData && (
                    <Card size="small">
                        <Skeleton loading={true} paragraph active />
                        <Skeleton loading={true} paragraph active />
                        <Skeleton loading={true} paragraph active />
                    </Card>
                )}

                {isSitesSelected && !isFetchingData && (
                    <>
                        <div className="energy-usage-reporting">
                            <EnergyDemandWidget
                                dateRange={chartDateRange}
                                energyData={chartEnergyData}
                                eventsData={chartEventsData}
                                baselineMode={baselineMarket.mode}
                                baselineWarning={baselineMarket.warning}
                            />
                            <EnergyUsageOverHourWidget
                                dateRange={chartDateRange}
                                energyData={chartEnergyData}
                                eventsData={chartEventsData}
                                baselineMode={baselineMarket.mode}
                            />
                            <EnergyConsumptionWidget dateRange={chartDateRange} energyData={chartEnergyData} />
                        </div>
                    </>
                )}

                <PageFooter />

                {modalView === 'greenbutton' && <UploadEnergyDataModal onClose={() => setModalView('none')} />}
                {modalView === 'sharepoint' && <UploadEnergyDataArchiveModal onClose={() => setModalView('none')} />}
                {modalView === 'dominion' && <UploadEnergyDataDominionModal onClose={() => setModalView('none')} />}
                {modalView === 'request-energy-usage' && <RequestEnergyUsageModal onClose={() => setModalView('none')} />}
            </Layout.Content>
        </Layout>
    );
};

interface IUploadEnergyDataMenuProps {
    handleModalViewChange: (view: TModalView) => void;
}

const UploadEnergyDataMenu: React.FC<IUploadEnergyDataMenuProps> = ({ handleModalViewChange }) => {
    const ability = useContext(AbilityContext);

    const uploadItems: MenuProps['items'] = [
        {
            label: 'Upload SCE Sharepoint ZIP',
            key: '1',
            onClick: () => handleModalViewChange('sharepoint'),
        },
        {
            label: 'Upload Greenbutton XML or CSV',
            key: '2',
            onClick: () => handleModalViewChange('greenbutton'),
        },
        {
            label: 'Upload Dominion XLSX or ZIP',
            key: '4',
            onClick: () => handleModalViewChange('dominion'),
        },
        ...(ability.can('request', 'EnergyUsage')
            ? [
                  {
                      label: 'Request Update from Utility',
                      key: '3',
                      onClick: () => handleModalViewChange('request-energy-usage'),
                  },
              ]
            : []),
    ];

    const uploadMenuProps = {
        items: uploadItems,
    };

    return (
        <Dropdown.Button
            size="large"
            key="upload-data"
            data-cy="upload-energy-data"
            menu={uploadMenuProps}
            onClick={() => handleModalViewChange('greenbutton')}
        >
            <UploadOutlined /> Manage Energy Data
        </Dropdown.Button>
    );
};

const useEnergyUsageSidebar = () => {
    const { isFiltersSidebarEnabled } = useAppContext();

    const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(ENERGY_USAGE_PAGE_CONST.localStorage.sidebarCollapsedKey, false);

    const layoutPaddingLeft = useMemo(() => {
        if (!isFiltersSidebarEnabled) return '0';

        return sidebarCollapsed ? '35px' : `${ENERGY_USAGE_PAGE_CONST.sitesPanelWidth}px`;
    }, [isFiltersSidebarEnabled, sidebarCollapsed]);

    return {
        sidebarCollapsed,
        setSidebarCollapsed,
        layoutPaddingLeft,
        isFiltersSidebarEnabled,
    };
};
